import { makeAutoObservable } from "mobx";
import {
  AIAgent,
  AIAgentChat,
  AIAgentChatAction,
  AIAgentLog,
  AIAgentTrace,
  AIAgentTraceSpan,
} from "../models";
import axios from "axios";
import bots from "./bots";
import { messageAdapter } from "../services/chathub";

type AgentLogsPaginate = {
  data: AIAgentLog[];
  total: number;
  last_page: number;
  current_page: number;
};

type AgentTracesPaginate = {
  data: AIAgentTrace[];
  total: number;
  last_page: number;
  current_page: number;
  per_page?: number;
};

type ChatsPaginate = {
  data: AIAgentChat[];
  total: number;
  last_page: number;
  current_page: number;
};

type ChatActionsPaginate = {
  data: AIAgentChatAction[];
  total: number;
  last_page: number;
  current_page: number;
};

// The most recently requested trace uuid. Module-scoped (not observable) — it is
// a request-ordering guard, not UI state — so a stale/out-of-order response (e.g.
// trace A resolving after trace B was selected) can be ignored.
let latestTraceUuid: string | undefined;

class AIAgentsStore {
  agents: AIAgent[] = [];
  selectedAgent?: AIAgent;
  agentLogsPaginate?: AgentLogsPaginate;
  agentTracesPaginate?: AgentTracesPaginate;
  selectedAgentTrace?: AIAgentTrace;
  chatsPaginate?: ChatsPaginate;
  chatActionsPaginate?: ChatActionsPaginate;

  constructor() {
    makeAutoObservable(this);
  }

  loadAgents = async () => {
    try {
      const {
        data: { response: data },
      } = await axios.get(
        `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent`,
        {
          params: {
            key: bots.selectedBot?.key,
            password: bots.selectedBot?.password,
          },
        }
      );

      this.setAgents(data.map(this.aIAgentAgentParser));
    } catch (error) {
      throw error;
    }
  };

  getAgent = async (uuid: string) => {
    try {
      const {
        data: { response: data },
      } = await axios.get(
        `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}`,
        {
          params: {
            key: bots.selectedBot?.key,
            password: bots.selectedBot?.password,
          },
        }
      );

      const agent = this.aIAgentAgentParser(data);
      this.setSelectedAgent(agent);

      return agent;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  createAgent = async (body: any) => {
    try {
      const {
        data: { response: data },
      } = await axios.post(
        `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent`,
        {
          name: body.name,
        },
        {
          params: {
            key: bots.selectedBot?.key,
            password: bots.selectedBot?.password,
          },
        }
      );

      this.setAgents([this.aIAgentAgentParser(data), ...this.agents]);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  updateAgent = async (uuid: string, body: AIAgent) => {
    try {
      const {
        data: { response: data },
      } = await axios.put(
        `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}`,
        {
          name: body.name,
          objective: body.objective,
          prompt: body.prompt,
          status: body.status,
          embedding_types: body.embeddingTypes,
          meta: body.meta,
          is_active: body.isActive,
          human_transfer_config: body.humanTransferConfig,
        },
        {
          params: {
            key: bots.selectedBot?.key,
            password: bots.selectedBot?.password,
          },
        }
      );

      this.setAgents(
        this.agents.map((agent) =>
          agent.uuid === uuid ? this.aIAgentAgentParser(data) : agent
        )
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  deleteAgent = async (uuid: string) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}`,
        {
          params: {
            key: bots.selectedBot?.key,
            password: bots.selectedBot?.password,
          },
        }
      );

      this.setAgents(this.agents.filter((agent) => agent.uuid !== uuid));
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  loadAgentLogs = async (uuid: string, page = 1, agentChatId?: number) => {
    if (!bots.selectedBot) {
      return;
    }

    const {
      data: { response: data },
    } = await axios.get(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}/log`,
      {
        params: {
          key: bots.selectedBot?.key,
          password: bots.selectedBot?.password,
          page,
          chat_id: agentChatId,
        },
      }
    );

    this.setAgentLogsPaginate({
      ...data,
      data: data.data.map(this.agentLogAdapter),
    });
  };

  loadAgentTraces = async (
    uuid: string,
    page = 1,
    agentChatId?: number,
    pageSize = 20
  ) => {
    if (!bots.selectedBot) {
      return;
    }

    const {
      data: { response: data },
    } = await axios.get(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}/trace`,
      {
        params: {
          key: bots.selectedBot?.key,
          password: bots.selectedBot?.password,
          page,
          page_size: pageSize,
          chat_id: agentChatId,
        },
      }
    );

    this.setAgentTracesPaginate({
      ...data,
      data: data.data.map((item: any) => this.agentTraceAdapter(item)),
    });
  };

  loadAgentTrace = async (uuid: string, traceUuid: string) => {
    if (!bots.selectedBot) {
      return;
    }

    latestTraceUuid = traceUuid;

    const {
      data: { response: data },
    } = await axios.get(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}/trace/${traceUuid}`,
      {
        params: {
          key: bots.selectedBot?.key,
          password: bots.selectedBot?.password,
        },
      }
    );

    // Latest-wins guard: a newer trace was selected while this one was loading.
    if (latestTraceUuid !== traceUuid) {
      return;
    }

    const trace = this.agentTraceAdapter(data);
    this.setSelectedAgentTrace({
      ...trace,
      input: data.input,
      output: data.output,
      metadata: data.metadata,
      error: data.error,
      spans: (data.spans ?? []).map((span: any) =>
        this.agentTraceSpanAdapter(span)
      ),
    });
  };

  loadChats = async (uuid: string, page = 1) => {
    if (!bots.selectedBot) {
      return;
    }

    const {
      data: { response: data },
    } = await axios.get(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}/chat`,
      {
        params: {
          key: bots.selectedBot?.key,
          password: bots.selectedBot?.password,
          page,
        },
      }
    );

    this.setChatsPaginate({
      ...data,
      data: data.data.map(this.chatAdapter),
    });
  };

  loadChatActions = async (uuid: string, id: number, page = 1) => {
    if (!bots.selectedBot) {
      return;
    }

    const {
      data: { response: data },
    } = await axios.get(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}/chat/${id}/action`,
      {
        params: {
          key: bots.selectedBot?.key,
          password: bots.selectedBot?.password,
          page,
        },
      }
    );

    this.setChatActionsPaginate({
      ...data,
      data: data.data.map(this.chatActionAdapter),
    });
  };

  loadChatMessages = async (uuid: string, id: number, pageSize: number) => {
    if (!bots.selectedBot) {
      return;
    }

    const {
      data: { response: data },
    } = await axios.get(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bots.selectedBot?.id}/ai-agent/${uuid}/chat/${id}/message`,
      {
        params: {
          key: bots.selectedBot?.key,
          password: bots.selectedBot?.password,
          page_size: pageSize,
        },
      }
    );

    return data.map(messageAdapter);
  };

  private setAgents(agents: AIAgent[]) {
    this.agents = agents;
  }

  private setSelectedAgent(agent: AIAgent) {
    this.selectedAgent = agent;
  }

  private setAgentLogsPaginate(paginate: AgentLogsPaginate) {
    this.agentLogsPaginate = paginate;
  }

  private setAgentTracesPaginate(paginate: AgentTracesPaginate) {
    this.agentTracesPaginate = paginate;
  }

  private setSelectedAgentTrace(trace: AIAgentTrace) {
    this.selectedAgentTrace = trace;
  }

  private setChatsPaginate(paginate: ChatsPaginate) {
    this.chatsPaginate = paginate;
  }

  private setChatActionsPaginate(paginate: ChatActionsPaginate) {
    this.chatActionsPaginate = paginate;
  }

  private aIAgentAgentParser(data: any): AIAgent {
    return {
      id: data.id,
      uuid: data.uuid,
      firebaseId: data.firebase_id,
      name: data.name,
      email: data.email,
      status: data.status,
      isActive: data.is_active,
      objective: data.objective,
      prompt: data.prompt,
      embeddingTypes: data.embedding_types,
      meta: data.meta,
      humanTransferConfig: data.human_transfer_config,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  private agentLogAdapter(data: any): AIAgentLog {
    return {
      id: data.id,
      aiAgentChatId: data.ai_agent_chat_id,
      level: data.level,
      type: data.type,
      content: data.content,
      meta: data.meta,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  private agentTraceAdapter(data: any): AIAgentTrace {
    return {
      id: data.id,
      uuid: data.uuid,
      botId: data.bot_id,
      aiAgentId: data.ai_agent_id,
      aiAgentChatId: data.ai_agent_chat_id,
      subscriberId: data.subscriber_id,
      triggerMessageId: data.trigger_message_id,
      engine: data.engine,
      status: data.status,
      input: data.input,
      output: data.output,
      metadata: data.metadata,
      error: data.error,
      durationMs: data.duration_ms,
      startedAt: data.started_at,
      endedAt: data.ended_at,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      spans: data.spans?.map((span: any) => this.agentTraceSpanAdapter(span)),
      usage: data.usage
        ? {
            promptTokens: data.usage.prompt_tokens,
            cachedPromptTokens: data.usage.cached_prompt_tokens,
            completionTokens: data.usage.completion_tokens,
            reasoningTokens: data.usage.reasoning_tokens,
            totalTokens: data.usage.total_tokens,
            llmCalls: data.usage.llm_calls,
            providerReportedCalls: data.usage.provider_reported_calls,
            complete: data.usage.complete,
            source: data.usage.source,
          }
        : undefined,
      costEstimate: data.cost_estimate
        ? {
            inputCost: data.cost_estimate.input_cost,
            cachedInputCost: data.cost_estimate.cached_input_cost,
            outputCost: data.cost_estimate.output_cost,
            totalCost: data.cost_estimate.total_cost,
            currency: data.cost_estimate.currency,
            complete: data.cost_estimate.complete,
            unpricedCalls: data.cost_estimate.unpriced_calls,
            pricingVersion: data.cost_estimate.pricing_version,
            pricingSource: data.cost_estimate.pricing_source,
            disclaimer: data.cost_estimate.disclaimer,
            byModel: (data.cost_estimate.by_model ?? []).map((item: any) => ({
              model: item.model,
              calls: item.calls,
              usage: {
                promptTokens: item.usage.prompt_tokens,
                cachedPromptTokens: item.usage.cached_prompt_tokens,
                completionTokens: item.usage.completion_tokens,
                reasoningTokens: item.usage.reasoning_tokens,
                totalTokens: item.usage.total_tokens,
              },
              inputCost: item.input_cost,
              cachedInputCost: item.cached_input_cost,
              outputCost: item.output_cost,
              totalCost: item.total_cost,
              ratesPerMillion: item.rates_per_million
                ? {
                    input: item.rates_per_million.input,
                    cachedInput: item.rates_per_million.cached_input,
                    output: item.rates_per_million.output,
                  }
                : undefined,
              longContextCalls: item.long_context_calls,
            })),
          }
        : undefined,
    };
  }

  private agentTraceSpanAdapter(data: any): AIAgentTraceSpan {
    return {
      id: data.id,
      uuid: data.uuid,
      traceId: data.trace_id,
      parentSpanId: data.parent_span_id,
      name: data.name,
      type: data.type,
      status: data.status,
      input: data.input,
      output: data.output,
      metadata: data.metadata,
      error: data.error,
      durationMs: data.duration_ms,
      startedAt: data.started_at,
      endedAt: data.ended_at,
      children: (data.children ?? []).map((child: any) =>
        this.agentTraceSpanAdapter(child)
      ),
    };
  }

  private chatAdapter(data: any): AIAgentChat {
    return {
      id: data.id,
      connectedFrom: data.connected_from,
      subscriberId: data.subscriber_id,
      connectedAt: data.connected_at,
      disconnectedAt: data.disconnected_at,
      meta: data.meta,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  private chatActionAdapter(data: any): AIAgentChatAction {
    return {
      id: data.id,
      triggerType: data.trigger_type,
      triggerMeta: data.trigger_meta,
      analysis: data.analysis,
      actions: data.actions,
      meta: data.meta,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export default new AIAgentsStore();
